import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { GenericConfirmationDialogComponent } from '../../generic-confirmation-dialog/generic-confirmation-dialog.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { DetectMethodChanges } from '../../utils';
import { LoggerService } from '../../logging/logger-factory.service';
import { Logger } from '../../logging/logger.interface';
import { MessageBusService } from '../../message-bus.service';
import { LastQuoteCacheService } from '../../last-quote-cache.service';
import { QuoteDto } from '../../shell-communication/dtos/quote-dto.class';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import {
   CollarsBucketDto,
   CollarsPositionDto,
   ListCollarsBuckets,
   ListCollarsPositions
} from '../../shell-communication/collars-operations';
import { parseOptionTicker } from '../../options-common/options.model';
import { CollarsQgCursorService } from '../collars-qg-cursor.service';
import { CollarsAnalysisLockService } from '../collars-analysis-lock.service';
import { CollarsAfterStateService } from './collars-after-state.service';
import { CollarsTransactionDiffService } from './collars-transaction-diff.service';
import {
   CollarsActualFillInput,
   CollarsAfterState,
   CollarsAfterStateLeg,
   CollarsApplyAfterStateConfig,
   CollarsApplyAfterStateResult,
   CollarsDiffResult,
   CollarsEditableLeg,
   CollarsGroupTransaction,
   CollarsTransactionDelta
} from './collars-after-state.models';
import { CollarsAfterStateRow, CollarsAfterStateRowsContext, toAfterStateRows } from './collars-after-state-rows';

// Bucket levels (match CollarsComponent / CollarsApplyAfterStateConfig.level).
const LEVEL_PORTFOLIO = 0;
const LEVEL_COMBO = 1;
const LEVEL_GROUP = 2;

/**
 * One operator-priced booking leg row in the Apply table. It carries the derived delta plus the display
 * Before(net) and After(end-state side/qty) context, and an editable ACTUAL average fill. legOrder sequences
 * the booking (close-first within a reverse/roll pair so the flatten books before the re-open).
 */
interface ApplyFillRow {
   groupBucketId: string;
   tradingInstrumentCode: string;
   kind: number;                                   // 0 = Asset, 1 = Option
   action: 'Open' | 'Close' | 'Increase' | 'Decrease';
   side: number;                                   // 1 = Buy, -1 = Sell (the booking-leg side)
   qty: number;                                    // positive magnitude to trade
   strike?: number;
   optionType?: string;
   expiration?: any;
   beforeNet: number;                              // current live net for this instrument in this group
   afterSide: number | null;                       // end-state side (null when the instrument is fully closed)
   afterQty: number;                               // end-state magnitude (0 when fully closed)
   legOrder: number;                               // booking order (ascending; close-first in reverse/roll)
   actualAvgPx: number;                            // operator-entered actual average fill price
   liveQuote: number;                              // last live reference quote (option=mid, asset=last); null until a tick arrives — REFERENCE ONLY
}

// Asset vs option leg (matches CollarsPositionDto.kind / RecordCollarsTransaction.kind / ApplyFillRow.kind).
const KIND_ASSET = 0;
const KIND_OPTION = 1;

/**
 * One leaf group inside a combo block. It pairs the CLONED after-state display row (Before / Transaction /
 * After — the exact hg-after-state look, built by toAfterStateRows) with the operator-priced fill rows for
 * that group's transaction legs. `display.transaction` and `fills` are derived from the SAME diff deltas in
 * the SAME order, so they are index-aligned: the i-th ACTUAL-FILL input sits on the i-th transaction leg.
 */
interface ApplyGroupEntry {
   groupBucketId: string;
   display: CollarsAfterStateRow;   // cloned Before/Transaction/After view (price-free, hg-after-state markup)
   fills: ApplyFillRow[];           // operator-priced booking legs, aligned 1:1 to display.transaction
   // Per-group ON/OFF (HG after-state parity): OFF = excluded from THIS apply only — not booked, key NOT
   // deleted, dimmed in the dialog. Session-only; every dialog open starts every group ON.
   enabled: boolean;
   // The saved plan failed diff validation (corrupt/legacy data, e.g. a non-positive qty). A blocked group
   // renders its section with the reason + the discard X — so the operator can delete the bad plan
   // IN-DIALOG — but contributes no booking rows and never gates the OTHER groups' Apply. (Previously a
   // single global blockReason from an invisible zero-row group locked Apply with no in-dialog remedy.)
   blocked?: boolean;
   blockReason?: string;
   // The group's combo is under analysis (CR-20260930-001): blocked with the lock reason, but the saved plan is
   // not the problem — so no "discard or fix it" advice, and the X stays (the plan may still be dropped).
   locked?: boolean;
}

/** A combo-grouped block of group entries (renders a header / separator above its groups). */
interface ApplyComboBlock {
   comboBucketId: string;
   comboName: string;
   groups: ApplyGroupEntry[];
}

/**
 * Apply After State dialog. Reads the saved end-state for the scope's leaf groups, diffs it against the LIVE
 * current positions (Before = live face value; NO snapshot, NO re-validation), shows
 * Before(live) + Transaction(derived) + After(end-state), the operator enters ACTUAL average fills, then it
 * books via RecordCollarsTransaction per leg (through AfterStateService.applyAfterState) and DELETES the
 * consumed after-state settings key(s).
 *
 * The transaction is derived by the SAME CollarsTransactionDiffService that backs the Make-Transaction
 * Preview — this dialog never re-implements the diff; it projects each leaf group's saved end-state to
 * editable rows, calls computeDiff(LIVE positions, projected end-state), and renders result.transaction.
 * Promise-based (backdate pattern).
 */
@Component({
   selector: 'ets-collars-apply-after-state-dialog',
   templateUrl: './collars-apply-after-state-dialog.component.html',
   styleUrls: ['./collars-apply-after-state-dialog.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsApplyAfterStateDialogComponent implements OnDestroy {

   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _toastr: ToastrService,
      private readonly _shellClient: ShellClientService,
      private readonly _afterState: CollarsAfterStateService,
      private readonly _diff: CollarsTransactionDiffService,
      private readonly _messageBus: MessageBusService,
      private readonly _lastQuoteCache: LastQuoteCacheService,
      private readonly _qgCursor: CollarsQgCursorService,
      private readonly _analysisLocks: CollarsAnalysisLockService,
      loggerService: LoggerService
   ) {
      this._logger = loggerService.createLogger('CollarsApplyAfterStateDialog');
      // QG history mode: the Expiration cells' "(Nd)" DTE counts from the cursor day (pipe fromDate
      // argument). Repaint when that day flips while the dialog is open — quote ticks repaint too,
      // but a paused stream would otherwise leave the text stale until the next interaction.
      this._qgCursor.dayChanged$
         .pipe(takeUntil(this._unsub))
         .subscribe(() => {
            if (this.isVisible) {
               this._changeDetector.detectChanges();
            }
         });
   }

   private readonly _logger: Logger;

   private readonly _unsub = new Subject<void>();

   // Live reference quotes: distinct tickers currently subscribed via the shared cache (one ref per row);
   // torn down on hide. _quotesUnsub closes the QuoteDto bus subscription that keeps liveQuote ticking.
   private _subscribedTickers: string[] = [];
   private _quotesUnsub: Subject<void> = null;

   // Centered number-box text (stable object — an inline template literal would re-create it every CD pass).
   readonly dxCenterTextStyleObj = { style: 'text-align: center' };

   // The discard (X) confirmation — the shared ETS confirmation dialog (resolve = Yes, reject = No).
   @ViewChild('applyConfirm', { static: false }) confirmDialog: GenericConfirmationDialogComponent;
   confirmTitle = 'Discard After State';

   isVisible = false;
   busy = false;

   // True once any bucket's saved plan was DISCARDED in this dialog session — the close result carries it
   // so the panel refreshes the green indicator even when nothing was booked.
   private _discarded = false;

   private _config: CollarsApplyAfterStateConfig;
   private _resolve: (result: CollarsApplyAfterStateResult) => void;

   // Whether the scope spans more than one combo (drives the combo separators in the template).
   multiCombo = false;

   // True once load produced at least one section; false => nothing to apply. (Blocked sections count —
   // they render with their reason + discard X; Apply enablement is gated by enabledRows(), not this.)
   hasFills = false;

   // The combo-grouped booking rows the operator prices.
   blocks: ApplyComboBlock[] = [];

   // NOTE: show() stays SYNCHRONOUS and returns the result Promise immediately (same shape as the backdate /
   // make-transaction dialogs). It must NOT be @DetectMethodChanges({isAsync:true}) — that decorator's
   // .then(() => {}) swallows the method's return value, so the caller's .then(result => ...) would receive
   // undefined. The async load runs separately (runLoad) and repaints the OnPush view via its own decorator.
   @DetectMethodChanges()
   show(config: CollarsApplyAfterStateConfig): Promise<CollarsApplyAfterStateResult> {
      this._config = config;
      this.blocks = [];
      this.multiCombo = false;
      this.hasFills = false;
      this._discarded = false;
      this.busy = true;
      this.isVisible = true;

      const promise = new Promise<CollarsApplyAfterStateResult>(resolve => { this._resolve = resolve; });
      // Fire-and-forget: populate the diff rows; runLoad owns its own busy reset + change detection.
      this.runLoad();
      return promise;
   }

   @DetectMethodChanges({ isAsync: true })
   async runLoad(): Promise<void> {
      try {
         await this.load();
      } catch (e) {
         this._logger.error('Collars apply after-state load failed', { error: e.stack || e });
         this._toastr.error('Could Not Load the Saved Plan');
         this.teardownLiveQuotes();
         this.blocks = [];
         this.hasFills = false;
      } finally {
         this.busy = false;
      }
   }

   @DetectMethodChanges()
   onHidden(): void {
      this.isVisible = false;
      this.teardownLiveQuotes();
      this.resolveWith({ applied: false, discarded: this._discarded });
   }

   @DetectMethodChanges()
   onCancel(): void {
      this.isVisible = false;
      this.teardownLiveQuotes();
      this.resolveWith({ applied: false, discarded: this._discarded });
   }

   // ---- Selective apply (HG after-state parity): per-group ON/OFF + per-group discard ----

   @DetectMethodChanges()
   onToggleGroup(grp: ApplyGroupEntry, enabled: boolean): void {
      // Inert while booking (mid-apply mutations are confusing) and on blocked sections (nothing to apply).
      if (this.busy || grp.blocked) { return; }
      grp.enabled = enabled;
   }

   // X = DISCARD the bucket's saved plan permanently (delete the settings key, never applied). Confirmed
   // through the shared ETS confirmation dialog (owner ruling — destructive, unlike the session-only OFF).
   @DetectMethodChanges({ isAsync: true })
   async onDiscardGroup(grp: ApplyGroupEntry): Promise<void> {
      // NEVER discard while a booking pass runs: applyAfterState reads each leg's saved key AT BOOKING TIME
      // (terminal resolution) — deleting it mid-apply would book the group's remaining legs with a null
      // terminal and void the plan-stays-intact-on-failure guarantee.
      if (this.busy) { return; }
      try {
         await this.confirmDialog.show([
            `Discard the saved After State of '${grp.display.label}'? ` +
            `Its planned transaction will be deleted and never applied.`
         ]);
      } catch (e) {
         return;   // user chose No / dismissed
      }
      await this._afterState.discard(grp.groupBucketId);
      this._discarded = true;
      // Drop the section (and its combo block when it empties); recompute the empty-state flag.
      this.blocks.forEach(b => { b.groups = b.groups.filter(g => g !== grp); });
      this.blocks = this.blocks.filter(b => b.groups.length > 0);
      this.multiCombo = this.blocks.length > 1;
      this.hasFills = this.blocks.some(b => b.groups.length > 0);
   }

   // ---- Load + diff -------------------------------------------------------------------------------------

   /**
    * Pull the live buckets + positions, find the leaf groups under the opened scope that carry a saved
    * end-state, diff each group's LIVE current positions vs its saved end-state (via computeDiff), then lay
    * the derived legs out as combo-grouped, operator-priced rows.
    */
   private async load(): Promise<void> {
      const buckets = (await this._shellClient.processQuery<CollarsBucketDto[]>(new ListCollarsBuckets())) || [];
      const positions = (await this._shellClient.processQuery<CollarsPositionDto[]>(new ListCollarsPositions())) || [];

      const bucketById: { [id: string]: CollarsBucketDto } = {};
      buckets.forEach(b => { bucketById[b.bucketId] = b; });

      // Leaf groups under the scope subtree that currently hold a saved end-state key.
      const savedGroupIds = this._afterState.listAfterStateGroupIds();
      const scopedGroupIds = savedGroupIds.filter(gid => this.isGroupUnderScope(gid, bucketById));

      // Combos under analysis (CR-20260930-001): their groups render BLOCKED with the lock reason instead of
      // booking rows — the Shell would refuse the fill anyway, and a refused command carries no text.
      let lockedCombos = new Set<string>();
      try {
         const comboIds = scopedGroupIds
            .map(gid => bucketById[gid] ? bucketById[gid].parentBucketId : null)
            .filter(id => !!id);
         if (comboIds.length > 0) { lockedCombos = await this._analysisLocks.lockedComboIds(comboIds); }
      } catch (e) {
         this._logger.warn('Collars apply after-state: analysis-lock read failed; the Shell decides', { error: e.stack || e });
      }

      this._logger.debug(
         `Collars apply after-state load -> scope=${this._config.scopeBucketId} level=${this._config.level} ` +
         `savedGroups=${savedGroupIds.length} scopedGroups=${scopedGroupIds.length}`
      );

      // Combo-grouped blocks keyed by the group's parent combo bucket id (preserving first-seen order).
      const blockByComboKey: { [comboKey: string]: ApplyComboBlock } = {};
      const comboOrder: string[] = [];
      let legOrderSeq = 0;

      // Display context for the cloned rows: bucket-id -> NAME (titles must show group names, never raw
      // ids) + group -> parent combo. Built from the bucket tree fetched above — no extra round-trip.
      const rowsContext: CollarsAfterStateRowsContext = { groupNames: {}, groupCombos: {} };
      buckets.forEach(b => { rowsContext.groupNames[b.bucketId] = b.name; });
      buckets.forEach(b => {
         if (b.level !== LEVEL_GROUP) { return; }
         const parentCombo = b.parentBucketId ? bucketById[b.parentBucketId] : null;
         if (parentCombo) {
            rowsContext.groupCombos[b.bucketId] = { comboBucketId: parentCombo.bucketId, comboName: parentCombo.name };
         }
      });

      scopedGroupIds.forEach(groupId => {
         const saved: CollarsAfterState | null = this._afterState.getAfterState(groupId);
         const endStateLegs: CollarsAfterStateLeg[] = saved ? (saved.legs || []) : [];

         // LIVE current OPEN positions of this group (face value): net != 0, not Interest, not archived.
         const livePositions = positions.filter(p =>
            p.groupId === groupId && p.netPosition !== 0 && !p.isInterest && !p.isArchived);

         // The SAME diff service that backs the Make-Transaction Preview. Project the saved end-state to
         // editable rows so the contract signature (editableLegs: CollarsEditableLeg[]) is honoured, then
         // read the derived transaction + after column straight off the result.
         const editableEndState = endStateLegs.map((l, i) => this.toEditableLeg(groupId, l, i));
         const result: CollarsDiffResult = this._diff.computeDiff(livePositions, editableEndState, groupId, LEVEL_GROUP);

         // An invalid saved plan (corrupt/legacy data) becomes a BLOCKED section: visible, discardable
         // in-dialog, contributing no booking rows — it must never gate the other groups' Apply.
         const groupLocked = !!(bucketById[groupId] && bucketById[groupId].parentBucketId
            && lockedCombos.has(bucketById[groupId].parentBucketId));
         const groupBlocked = groupLocked || !!(result && !result.isValid);
         const groupBlockReason = groupLocked
            ? CollarsAnalysisLockService.LOCK_REASON
            : (groupBlocked ? (result.blockReason || 'The saved plan is invalid') : null);

         // Index live net (Before) and end-state side/qty (After) per instrument for the display columns.
         const netByCode: { [code: string]: number } = {};
         livePositions.forEach(p => { netByCode[p.tradingInstrumentCode] = p.netPosition; });
         // After column = the persisted end-state legs (the source of truth for "After"). result.after is the
         // diff's projection of the same set; fall back to the saved legs when it is empty.
         const afterLegs = (result && result.after && result.after.length) ? result.after : endStateLegs;
         const endByCode: { [code: string]: CollarsAfterStateLeg } = {};
         afterLegs.forEach(l => { endByCode[l.tradingInstrumentCode] = l; });

         const group = bucketById[groupId];
         const comboId = group ? group.parentBucketId : null;
         const combo = comboId ? bucketById[comboId] : null;
         const comboKey = comboId || ('__nocombo__' + groupId);

         let block = blockByComboKey[comboKey];
         if (!block) {
            block = { comboBucketId: comboKey, comboName: combo ? combo.name : (group ? group.name : '—'), groups: [] };
            blockByComboKey[comboKey] = block;
            comboOrder.push(comboKey);
         }

         // result.transaction is grouped per leaf group; each group's deltas are already close-first for a
         // reverse/roll pair (Close emitted before the matching Open). Preserve that order via legOrderSeq.
         const groupTxns: CollarsGroupTransaction[] = (result && result.transaction) ? result.transaction : [];
         const deltas: CollarsTransactionDelta[] = [];
         groupTxns.forEach(gt => { (gt.deltas || []).forEach(d => deltas.push(d)); });

         // Skip groups whose live state already matches the saved end-state (no derived transaction) —
         // but KEEP blocked groups: their section renders the reason + the discard X.
         if (deltas.length === 0 && !groupBlocked) { return; }

         // Operator-priced fill rows, one per derived delta, in the diff's (close-first) order. These drive
         // applyAfterState; they are NOT rendered as a separate table — each one's ACTUAL-FILL input is laid
         // over the aligned cloned Transaction leg in the template.
         const fills: ApplyFillRow[] = deltas.map(delta => {
            const endLeg = endByCode[delta.tradingInstrumentCode];
            return {
               groupBucketId: delta.groupBucketId || groupId,
               tradingInstrumentCode: delta.tradingInstrumentCode,
               kind: delta.kind,
               action: delta.action,
               side: delta.side,
               qty: delta.qty,
               strike: delta.strike,
               optionType: delta.optionType,
               expiration: delta.expiration,
               beforeNet: netByCode[delta.tradingInstrumentCode] || 0,
               // After: when the instrument survives in the end-state, show its side/qty; a Close-to-flat
               // leg whose instrument is absent from the end-state shows no surviving holding.
               afterSide: endLeg ? endLeg.side : null,
               afterQty: endLeg ? endLeg.qty : 0,
               legOrder: legOrderSeq++,
               actualAvgPx: null,
               liveQuote: null
            } as ApplyFillRow;
         });

         // Cloned Before/Transaction/After display row (hg-after-state look), PRICE-FREE. Built by the shared
         // mapper off the same diff result so display.transaction is index-aligned with `fills` above. The
         // context resolves the group NAME for the row title (never the raw id); the combo header names the
         // combo separately.
         const displayRows = toAfterStateRows(result, rowsContext);
         const display: CollarsAfterStateRow = (displayRows && displayRows.length > 0)
            ? displayRows[0]
            : {
               groupBucketId: groupId,
               label: group ? group.name : 'Unnamed group',
               isAsset: false,
               unchanged: false,
               before: [], transaction: [], after: []
            };

         block.groups.push({
            groupBucketId: groupId,
            display,
            fills,
            // Blocked groups start (and stay) excluded — nothing bookable; the X is their only action.
            enabled: !groupBlocked,
            blocked: groupBlocked,
            blockReason: groupBlockReason,
            locked: groupLocked
         });
      });

      this.blocks = comboOrder.map(k => blockByComboKey[k]).filter(b => b.groups.length > 0);
      this.multiCombo = this.blocks.length > 1;
      this.hasFills = this.blocks.some(b => b.groups.length > 0);

      // Live reference quotes (REFERENCE ONLY — the operator still types the actual avg fill). Subscribe each
      // booking leg's instrument through the SAME shared quote cache the panel uses; seed from the cache and
      // keep ticking off the QuoteDto bus.
      this.subscribeLiveQuotes();
   }

   // ---- Live reference quotes (mirror collars.component.ts blotter pattern) ------------------------------

   /**
    * Subscribe each booking leg's instrument for a live reference quote via the shared LastQuoteCacheService,
    * seed the row from the cache's current value, and keep the rows live off the 'QuoteDto' bus. The row's
    * tradingInstrumentCode IS the subscribable ticker (option = composed option ticker, asset = underlying) —
    * the same code carried straight from the diff delta. Distinct tickers only (ref-counted by the cache).
    */
   private subscribeLiveQuotes(): void {
      this.teardownLiveQuotes();

      const rows = this.allRows();
      if (rows.length === 0) { return; }

      // Distinct tickers (one subscription per instrument even if it appears on several rows, e.g. a reverse).
      const seen: { [ticker: string]: boolean } = {};
      const tickers: string[] = [];
      rows.forEach(r => {
         const t = r.tradingInstrumentCode;
         if (t && !seen[t]) { seen[t] = true; tickers.push(t); }
      });
      this._subscribedTickers = tickers;

      // Seed each row from whatever the cache already holds (subscribeTicker returns the last cached quote).
      tickers.forEach(ticker => {
         const seed = this._lastQuoteCache.subscribeTicker(ticker);
         if (seed) { this.applyQuoteToRows(seed); }
      });

      // Keep ticking off the shared bus; @DetectMethodChanges repaints the OnPush view on each batch.
      this._quotesUnsub = new Subject<void>();
      this._messageBus.of<QuoteDto[]>('QuoteDto')
         .pipe(takeUntil(this._quotesUnsub))
         .subscribe((m: { payload: QuoteDto[] }) => this.onLiveQuotes(m.payload));
   }

   /** Live reference ticks arrive on the 'QuoteDto' bus; update matching rows and repaint. */
   @DetectMethodChanges()
   private onLiveQuotes(quotes: QuoteDto[]): void {
      if (!this.isVisible || !quotes || quotes.length === 0) { return; }
      for (let i = 0; i < quotes.length; i++) {
         this.applyQuoteToRows(quotes[i]);
      }
   }

   /** Push one quote into every row that holds its ticker (option rows show MID, asset rows show LAST). */
   private applyQuoteToRows(q: QuoteDto): void {
      if (!q || !q.ticker) { return; }
      this.blocks.forEach(block => {
         block.groups.forEach(grp => {
            grp.fills.forEach(row => {
               if (row.tradingInstrumentCode === q.ticker) {
                  row.liveQuote = this.pickQuote(q, row);
               }
            });
         });
      });
   }

   /** Reference-price convention (mirrors the blotter): option legs show MID, asset legs show LAST. */
   private pickQuote(q: QuoteDto, row: ApplyFillRow): number {
      if (!q) { return null; }
      if (row.kind === KIND_OPTION) {
         return (typeof q.mid === 'number') ? q.mid : null;
      }
      return (typeof q.lastPx === 'number') ? q.lastPx : null;
   }

   /** Drop every live-quote subscription and close the bus subscription (called on hide / before re-load). */
   private teardownLiveQuotes(): void {
      if (this._quotesUnsub) {
         this._quotesUnsub.next();
         this._quotesUnsub.complete();
         this._quotesUnsub = null;
      }
      for (let i = 0; i < this._subscribedTickers.length; i++) {
         this._lastQuoteCache.unsubscribeTicker(this._subscribedTickers[i]);
      }
      this._subscribedTickers = [];
   }

   /**
    * Project one saved end-state leg to the CollarsEditableLeg shape computeDiff consumes. The saved
    * end-state is the desired holding (not crossed-out, not new relative to itself); qty stays a positive
    * magnitude. originalQty/targetQty are left for computeDiff to reconcile against the live positions.
    */
   private toEditableLeg(groupBucketId: string, leg: CollarsAfterStateLeg, seq: number): CollarsEditableLeg {
      return {
         id: seq,
         isNew: false,
         crossedOut: false,
         restorable: false,
         groupBucketId,
         kind: leg.kind,
         tradingInstrumentCode: leg.tradingInstrumentCode,
         side: leg.side,
         qty: leg.qty,
         targetQty: leg.qty,
         optionType: leg.optionType,
         strike: leg.strike,
         expiration: leg.expiration
      };
   }

   // ---- View helpers ------------------------------------------------------------------------------------

   // The QG cursor day the leg's Expiration "(Nd)" counts from — keyed by the leg's UNDERLYING (the
   // cursor is per-stream). Undefined outside QG history (the pipe then falls back to the wall clock).
   // parseOptionTicker caches parses, so the per-CD template call stays cheap; asset legs parse to
   // undefined and never reach the lookup.
   qgCursorDayOf(leg: { ticker?: string }): string {
      const t = parseOptionTicker(leg ? leg.ticker : null);
      return t ? this._qgCursor.cursorDayFor(t.underlying) : undefined;
   }

   ngOnDestroy(): void {
      this._unsub.next();
      this._unsub.complete();
      // The panel can die while this dialog is visible (DevExtreme fires no onHidden on dispose) —
      // without this the 'QuoteDto' bus subscription and the per-ticker quote-cache refcounts leak.
      this.teardownLiveQuotes();
   }

   private allRows(): ApplyFillRow[] {
      const out: ApplyFillRow[] = [];
      this.blocks.forEach(b => b.groups.forEach(g => g.fills.forEach(r => out.push(r))));
      return out;
   }

   // The booking rows of ENABLED groups only — switched-off groups are excluded from validation + Apply
   // (their keys survive untouched; HG selective-apply parity).
   private enabledRows(): ApplyFillRow[] {
      const out: ApplyFillRow[] = [];
      this.blocks.forEach(b => b.groups.forEach(g => {
         if (!g.enabled) { return; }
         g.fills.forEach(r => out.push(r));
      }));
      return out;
   }

   // Per-keystroke fill commit (the box sets valueChangeEvent="input"): the canApply() gate reads
   // actualAvgPx, and the DX default 'change' commit left it stale (and the Apply button dead) until
   // the operator blurred the box — in QG mode nothing else repaints to hide that.
   @DetectMethodChanges()
   onFillPxChange(grp: ApplyGroupEntry, index: number, value: number): void {
      grp.fills[index].actualAvgPx = value;
   }

   canApply(): boolean {
      if (this.busy || !this.hasFills) { return false; }
      const rows = this.enabledRows();
      if (rows.length === 0) { return false; }   // everything switched off / blocked -> nothing to apply
      // Every ENABLED booking leg needs a positive actual fill before Apply; OFF/blocked groups don't block.
      return rows.every(r => r.actualAvgPx != null && r.actualAvgPx > 0);
   }

   async onApply(): Promise<void> {
      if (!this.canApply()) { return; }
      this.busy = true;
      this._changeDetector.detectChanges();
      try {
         // Build the fills close-first (ascending legOrder, the order the diff emitted), stamping each with
         // the operator's actual price. One CollarsActualFillInput per booking leg. ENABLED groups only —
         // a switched-off group is not booked and keeps its saved key for a later session.
         const fills: CollarsActualFillInput[] = this.enabledRows()
            .slice()
            .sort((a, b) => a.legOrder - b.legOrder)
            .map(r => ({
               groupBucketId: r.groupBucketId,
               tradingInstrumentCode: r.tradingInstrumentCode,
               kind: r.kind,
               side: r.side,
               qty: r.qty,
               actualAvgPx: r.actualAvgPx,
               legOrder: r.legOrder
            }));

         this._logger.debug(`Collars apply after-state -> scope=${this._config.scopeBucketId} level=${this._config.level} fills=${fills.length}`);
         await this._afterState.applyAfterState(this._config.scopeBucketId, fills);
         this.isVisible = false;
         this.teardownLiveQuotes();
         this.resolveWith({ applied: true, discarded: this._discarded });
      } catch (e) {
         this._logger.error('Collars apply after-state failed', { error: e.stack || e });
         this._toastr.error('Apply Failed');
      } finally {
         this.busy = false;
         this._changeDetector.detectChanges();
      }
   }

   // ---- Subtree test ------------------------------------------------------------------------------------

   /** Is the leaf group a descendant of (or equal to) the opened scope bucket? */
   private isGroupUnderScope(groupId: string, bucketById: { [id: string]: CollarsBucketDto }): boolean {
      if (this._config.level === LEVEL_GROUP) {
         return groupId === this._config.scopeBucketId;
      }
      const group = bucketById[groupId];
      if (!group) { return false; }
      if (this._config.level === LEVEL_COMBO) {
         return group.parentBucketId === this._config.scopeBucketId;
      }
      // Portfolio scope: the group's combo's parent is the scope portfolio.
      if (this._config.level === LEVEL_PORTFOLIO) {
         const combo = group.parentBucketId ? bucketById[group.parentBucketId] : null;
         return !!combo && combo.parentBucketId === this._config.scopeBucketId;
      }
      return false;
   }

   private resolveWith(result: CollarsApplyAfterStateResult): void {
      if (this._resolve) {
         this._resolve(result);
         this._resolve = null;
      }
   }
}
